import { useEffect, useRef, useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog"
import { VisuallyHidden } from "@radix-ui/react-visually-hidden"
import { Spinner } from "@/components/ui/spinner"

interface DemoBookingModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

// Landing page identifier - this will be passed to Zoho Booking
const LANDING_PAGE = "BridgeGate N8N"

// UTM parameters to track
const UTM_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'referral_page'
]

export function DemoBookingModal({ open, onOpenChange }: DemoBookingModalProps) {
  const [bookingUrl, setBookingUrl] = useState<string>("")
  const [isLoading, setIsLoading] = useState(true)
  const loadCountRef = useRef(0)
  const iframeRef = useRef<HTMLIFrameElement>(null)

  // Close modal and route to the standalone thank-you page.
  // Optionally accepts a hash-search payload (e.g. "?booking_id=...") that
  // Zoho appended to its custom confirmation URL, so the ThankYouPage can
  // render the booking details.
  const goToThankYou = (innerSearch = "") => {
    onOpenChange(false)
    const target = `${window.location.pathname}${window.location.search}#/thank-you${innerSearch}`
    // Use replace so the modal page isn't left in browser history.
    window.location.replace(target)
  }

  useEffect(() => {
    if (!open) return

    // Detection strategy: Zoho Bookings is in a cross-origin iframe so we can't
    // read its DOM/URL. We listen for any postMessage from the booking origin
    // and look for confirmation indicators (booking id, appointment time, etc.).
    const onMessage = (event: MessageEvent) => {
      try {
        const origin = event.origin || ""
        if (!/(^|\.)vorro\.net$|zoho|booknow/i.test(origin)) return
        // Log to help diagnose what Zoho actually posts so we can tighten this.
        // Safe to leave in: only fires for messages from the booking origin.
        // eslint-disable-next-line no-console
        console.debug("[demo-booking] message from", origin, event.data)
        const raw =
          typeof event.data === "string"
            ? event.data
            : JSON.stringify(event.data ?? "")
        if (
          /book(ed|ing)|appointment|confirm|success|all[\s_-]*set|thank|complete/i.test(
            raw,
          )
        ) {
          goToThankYou()
        }
      } catch {
        /* ignore */
      }
    }
    window.addEventListener("message", onMessage)
    return () => window.removeEventListener("message", onMessage)
  }, [open])

  useEffect(() => {
    if (open) {
      // Store UTM params from URL in sessionStorage
      const params = new URLSearchParams(window.location.search)
      UTM_PARAMS.forEach(p => {
        const value = params.get(p)
        if (value) {
          sessionStorage.setItem(p, value)
        }
      })

      // Build Zoho booking URL with parameters.
      // NOTE: the `/portal-embed` path is required — Zoho serves the non-embed
      // URL with X-Frame-Options/frame-ancestors that block iframing.
      let url = 'https://booknow.vorro.net/portal-embed#/Ayanika?'
      
      // Append stored UTMs
      UTM_PARAMS.forEach(p => {
        const value = sessionStorage.getItem(p)
        if (value) {
          url += `${p}=${encodeURIComponent(value)}&`
        }
      })
      
      // Add landing page identifier
      url += `landing_page=${encodeURIComponent(LANDING_PAGE)}&`
      
      // Add full referral URL
      url += `referral_link=${encodeURIComponent(window.location.href)}`

      setBookingUrl(url)
      setIsLoading(true)
      loadCountRef.current = 0
    }
  }, [open])

  const handleIframeLoad = () => {
    setIsLoading(false)
    loadCountRef.current += 1

    // After Zoho's "Show your own confirmation page" redirect, the iframe
    // lands on our same-origin thank-you URL. As soon as it's same-origin
    // we can read its location and lift the navigation out of the iframe —
    // this is more reliable than the ThankYouPage frame-busting itself,
    // since cross-origin top-navigation can be silently blocked without a
    // user gesture.
    try {
      const win = iframeRef.current?.contentWindow
      const href = win?.location?.href
      if (href && /[#/]thank-you/i.test(href)) {
        const inner = new URL(href)
        const hashQIdx = inner.hash.indexOf("?")
        const innerSearch =
          hashQIdx >= 0 ? inner.hash.slice(hashQIdx) : inner.search || ""
        goToThankYou(innerSearch)
      }
    } catch {
      /* cross-origin — still on Zoho's domain, ignore until next load */
    }
  }

  const handleClose = (openState: boolean) => {
    onOpenChange(openState)
    if (!openState) {
      setIsLoading(true)
      loadCountRef.current = 0
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="!max-w-[1140px] w-[1140px] h-auto p-0 rounded-lg border border-gray-200 shadow-2xl overflow-hidden !bg-white" showCloseButton={true}>
        <VisuallyHidden>
          <DialogTitle>Book a Demo</DialogTitle>
          <DialogDescription>Schedule a demo with our BridgeGate N8N experts using Zoho Booking</DialogDescription>
        </VisuallyHidden>

        <div className="relative">
          {/* Loading spinner */}
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white z-10" style={{ minWidth: '1100px', minHeight: '580px' }}>
              <div className="flex flex-col items-center gap-3">
                <Spinner className="h-8 w-8 text-[#a63083]" />
                <p className="text-sm text-[#6B6B7B]">Loading booking calendar...</p>
              </div>
            </div>
          )}

          {/* Zoho Booking iframe - width 1100px triggers Zoho's 3-column layout */}
          {bookingUrl && (
            <iframe
              ref={iframeRef}
              src={bookingUrl}
              frameBorder="0"
              allowFullScreen
              onLoad={handleIframeLoad}
              style={{ width: '1100px', height: '580px', border: 'none' }}
              title="Book a Demo"
            />
          )}

        </div>
      </DialogContent>
    </Dialog>
  )
}
